import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Database, CheckCircle, Clock, TrendingUp, Plus, RefreshCw, RotateCcw, Bell, Activity, Pause, Play, Trash2, Eye } from "lucide-react";
import Header from "../components/layout/Header";
import { SearchInput } from "../components/ui/index";
import { fonctionsAPI, cascadeAPI, projetsAPI } from "../api/services";
import { useAuth } from "../context/AuthContext";
import { buildFonctionPreview } from "../utils/naturalLanguage";

const typeBadgeColor = {
  "Prédiction": "bg-blue-100 text-blue-700",
  "Tendance": "bg-purple-100 text-purple-700",
  "Analyse": "bg-green-100 text-green-700",
};

export default function Fonctions() {
  const { t } = useTranslation();
  const { user, canDo, adminMode } = useAuth();
  const isMembre = user?.role === 'membre';
  const navigate = useNavigate();
  const [search, setSearch]               = useState("");
  const [filterRevendeur, setFilterRevendeur] = useState("");
  const [filterClient, setFilterClient]   = useState("");
  const [filterProjet, setFilterProjet]   = useState("");

  // Data state
  const [fonctions, setFonctions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cascade filter state
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projets, setProjets] = useState([]);

  const filtersSelected = !!filterProjet;

  // Fetch fonctions
  const fetchFonctions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterRevendeur) params.revendeur = filterRevendeur;
      if (filterClient) params.client = filterClient;
      if (filterProjet) params.projet = filterProjet;

      const { data } = await fonctionsAPI.getAll(params);
      const list = data.data || data;
      setFonctions(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Erreur chargement fonctions:", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterRevendeur, filterClient, filterProjet]);

  useEffect(() => { fetchFonctions(); }, [fetchFonctions]);

  // Membre : charger ses projets IoT (directs + via chantiers)
  useEffect(() => {
    if (!isMembre) return;
    projetsAPI.mesProjetsIoT().then(res => {
      setProjets(res.data.data || res.data || []);
    }).catch(console.error);
  }, [isMembre]);

  // Admin/Revendeur : cascade classique
  useEffect(() => {
    if (isMembre) return;
    cascadeAPI.revendeurs().then(res => {
      setRevendeurs(res.data.data || res.data || []);
    }).catch(console.error);
  }, [isMembre]);

  useEffect(() => {
    if (isMembre) return;
    cascadeAPI.clients(filterRevendeur || undefined).then(res => {
      setClients(res.data.data || res.data || []);
    }).catch(console.error);
  }, [filterRevendeur, isMembre]);

  useEffect(() => {
    if (isMembre) return;
    cascadeAPI.projets(filterClient || undefined, filterRevendeur || undefined).then(res => {
      setProjets(res.data.data || res.data || []);
    }).catch(console.error);
  }, [filterClient, filterRevendeur, isMembre]);

  const handleRevendeurChange = (v) => { setFilterRevendeur(v); setFilterClient(""); setFilterProjet(""); };
  const handleClientChange    = (v) => { setFilterClient(v); setFilterProjet(""); };
  const reset = () => { setFilterRevendeur(""); setFilterClient(""); setFilterProjet(""); setSearch(""); };

  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleToggle = async (id) => {
    try {
      await fonctionsAPI.toggle(id);
      fetchFonctions();
    } catch (err) {
      console.error("Erreur toggle:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fonctionsAPI.remove(id);
      setConfirmDelete(null);
      fetchFonctions();
    } catch (err) {
      console.error("Erreur suppression:", err);
    }
  };

  const total      = fonctions.length;
  const actives    = fonctions.filter(f => f.statut === "Actif").length;
  const enPause    = fonctions.filter(f => f.statut === "En pause").length;
  const executions = fonctions.reduce((s, f) => s + (f.executions || 0), 0);

  const selectStyle = "flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <>
      <Header title={t('functions.title')} subtitle={t('functions.subtitle')} />

      {/* Titre section */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
          <Database size={16} className="text-teal-600" />
        </div>
        <h2 className="font-semibold text-slate-800">{t('functions.title')}</h2>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
        <div className="flex gap-3 mb-3">
          {adminMode === 'platform' && (
            <select value={filterRevendeur} onChange={e => handleRevendeurChange(e.target.value)} className={selectStyle}>
              <option value="">{t('common.allResellers')} ({revendeurs.length})</option>
              {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
            </select>
          )}
          {!isMembre && (
            <select value={filterClient} onChange={e => handleClientChange(e.target.value)} className={selectStyle}>
              <option value="">{t('common.allClients')} ({clients.length})</option>
              {clients.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
            </select>
          )}
          <select value={filterProjet} onChange={e => setFilterProjet(e.target.value)} className={selectStyle}>
            <option value="">{t('common.allProjects')} ({projets.length})</option>
            {projets.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Rechercher..." />
          </div>
          {(filterRevendeur || filterClient || filterProjet || search) && (
            <button onClick={reset} className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
              <RefreshCw size={14} /> {t('functions.reset')}
            </button>
          )}
        </div>
      </div>

      {/* Stats + bouton */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        {[
          { icon: Database,    bg: "bg-teal-100",   color: "text-teal-600",   label: t('functions.totalFunctions'), value: total },
          { icon: CheckCircle, bg: "bg-green-100",  color: "text-green-600",  label: t('functions.active'),         value: actives },
          { icon: Clock,       bg: "bg-yellow-100", color: "text-yellow-500", label: t('functions.paused'),        value: enPause },
          { icon: TrendingUp,  bg: "bg-blue-100",   color: "text-blue-600",   label: t('functions.executions'),      value: executions },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <s.icon size={18} className={s.color} />
            </div>
            <div>
              <p className="text-xs text-slate-400">{s.label}</p>
              <p className="text-2xl font-bold text-slate-800">{s.value}</p>
            </div>
          </div>
        ))}
        {canDo('configIoT') && (
          <button
            disabled={!filtersSelected}
            onClick={() => navigate(`/fonctions/creer?revendeur=${filterRevendeur}&client=${filterClient}&projet=${filterProjet}`)}
            className={`rounded-2xl p-4 flex items-center justify-center gap-2 font-semibold text-sm transition-all ${
              filtersSelected
                ? "bg-teal-600 text-white hover:bg-teal-700 cursor-pointer"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Plus size={18} /> {t('functions.create')}
          </button>
        )}
      </div>

      {/* Liste fonctions */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <RefreshCw size={32} className="text-gray-300 mx-auto mb-3 animate-spin" />
          <p className="text-slate-400">{t('functions.loading')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {fonctions.length > 0 ? fonctions.map(f => (
            <div key={f._id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Database size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    {/* Titre + badges */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold text-slate-800">{f.nom}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-slate-600 border border-gray-200">🏢 {f.revendeur?.nom}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">👤 {f.client?.nom}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">📁 {f.projet?.nom}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 flex items-center gap-1">
                        <CheckCircle size={10} /> {f.statut}
                      </span>
                    </div>
                    {/* Description */}
                    <p className="text-sm text-slate-500 italic mb-3">"{f.description}"</p>
                    {/* Aperçu langage naturel */}
                    {(() => {
                      const preview = buildFonctionPreview(f);
                      if (!preview) return null;
                      return (
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[9px] bg-teal-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">Langage naturel</span>
                          </div>
                          <p className="text-xs text-slate-700 leading-relaxed">
                            <span className="font-semibold text-teal-700">SI </span>
                            <span className="text-blue-600">{preview.conditionText}</span>
                          </p>
                          <p className="text-xs text-slate-700 leading-relaxed mt-0.5">
                            <span className="font-semibold text-orange-600">ALORS </span>
                            <span>{preview.actionText}</span>
                          </p>
                        </div>
                      );
                    })()}
                    {/* Tags période + type */}
                    <div className="flex items-center gap-3 mb-2">
                      <span className="flex items-center gap-1 text-xs text-slate-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">
                        <Clock size={11} /> {t('functions.period')} {f.periode}
                      </span>
                      <span className={`text-xs px-2.5 py-1 rounded-lg font-medium ${typeBadgeColor[f.type] || ""}`}>
                        <TrendingUp size={11} className="inline mr-1" />{f.type}
                      </span>
                    </div>
                    {/* Stats */}
                    <div className="flex items-center gap-5 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><RotateCcw size={12} /> {f.executions || 0} {t('functions.executionsLabel')}</span>
                      <span className="flex items-center gap-1"><Bell size={12} /> {t('functions.lastExecution')} {f.dernier || "—"}</span>
                      <span className="flex items-center gap-1"><Activity size={12} /> {f.noeuds || 0} {t('functions.nodesLabel')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => navigate(`/fonctions/creer?id=${f._id}&projet=${f.projet?._id || ""}`)}
                    className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50"
                  >
                    <Eye size={14} /> {t('functions.view')}
                  </button>
                  <button
                    onClick={() => handleToggle(f._id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium ${
                      f.statut === "Actif"
                        ? "bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100"
                        : "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                    }`}
                  >
                    {f.statut === "Actif" ? <><Pause size={14} /> {t('functions.pause')}</> : <><Play size={14} /> {t('functions.activate')}</>}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(f._id)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm hover:bg-red-100"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          )) : (
            <div className="bg-white rounded-2xl p-16 text-center border border-dashed border-gray-200">
              <Database size={48} className="text-gray-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium text-lg">{t('functions.noFunctions')}</p>
              <p className="text-slate-400 text-sm mt-1">
                {filtersSelected ? t('functions.createFirst') : t('functions.selectProjectHint')}
              </p>
            </div>
          )}
        </div>
      )}
      {/* Modal confirmation suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full mx-4">
            <h3 className="font-semibold text-slate-800 mb-2">{t('functions.deleteFunction')}</h3>
            <p className="text-sm text-slate-500 mb-5">{t('functions.irreversible')}</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">{t('common.cancel')}</button>
              <button onClick={() => handleDelete(confirmDelete)} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm hover:bg-red-700">{t('common.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
